<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import CloseupIndowInsertCreatingCompressionSealImage from './images/closeup_indow_insert_creating_compression_seal.jpg';
	import ManSealingStormWindows from './images/man_sealing_storm_windows.jpg';
	import WomanWalkingByRestoredWindowImage from './images/woman_walking_by_restored_window.jpg';
</script>

<Section>
	<p>
		The performance of storm windows vs regular windows can depend on what
		windows you have to start out with. In this blog we’ll cover typical
		replacement windows and storm windows, what insulation you can expect from
		each and what noise abatement performance to expect out of each category.
	</p>
	<div>
		<p class="font-bold">Storm Windows</p>
		<p>
			Storm windows are a second window pane added to the outside or inside of
			your already existing window. Exterior storm windows attach to the outside
			of your window frame and interior storm windows to the inside of your
			window frame.
		</p>
	</div>
	<div>
		<p class="font-bold">Replacement Windows</p>
		<p>
			Replacement windows replace your already existing windows – either by
			installing a new type of window style or technology, or because the seal
			on the old window has failed.
		</p>
	</div>
	<Image
		alt="close up of one of indow window insert options"
		src={CloseupIndowInsertCreatingCompressionSealImage}
	/>
</Section>

<Section>
	<h2>Replacement Window Options</h2>
	<p>
		The most popular replacement windows are double hung and casement, wood
		frame, vinyl frame, or fiberglass frame. Casement style windows are more
		airtight than double hung, so they will provide more insulation and defense
		against drafts than double hung. Double or triple glazing adds an extra pane
		of glass with air or gas between them. This provides better insulation and
		sound reduction (especially triple glazing), but can be very expensive.
		Low-E coating reflects heat to improve efficiency. Read the <a
			href="https://www.consumerreports.org/cro/replacement-windows/buying-guide/index.htm"
			target="_blank"
			rel="external"
		>
			consumer report on replacement windows
		</a>.
	</p>
	<p>
		“Star-qualified windows can lower your energy bill by an average of 12
		percent. That’s only $27 to $111 a year for a 2,000-square-foot single-story
		home with storm windows or double-pane windows, $126 to $465 if your home
		has just single-pane windows, according to <a
			href="https://www.energystar.gov/products/building_products/residential_windows_doors_and_skylights/benefits"
			target="_blank"
			rel="external">Energy Star</a
		>.” claims the
		<a
			href="https://www.consumerreports.org/cro/replacement-windows/buying-guide/index.htm"
			target="_blank"
			rel="external">replacement window consumer report</a
		>. Interior storm windows can save an average of 20% on energy bills
		according to the
		<a href="/solutions/reduce-energy-waste">third party studies</a>.
	</p>
	<Image
		alt="indow storm windows vs regular windows"
		src={ManSealingStormWindows}
	/>
</Section>

<Section>
	<h2>Storm Window Options</h2>
	<p>
		Adding an interior storm window to a double-pane window can reduce outside
		noise up to 50% (10-12 <a
			href="/resources/faq/interior-storm-window-inserts-glossary#acoustic"
			>dBA</a
		>). Adding an interior storm window to a single-pane window can reduce noise
		up to 70% (18.9 dBA). Replacement windows reduce outside noise up to 20%.
		This data is based on
		<a href="/solutions/noise-performance">third party studies</a> performed with
		Indow window inserts. Because the insert is made of acrylic, it blocks sound better
		than standard window glass. A near airtight seal is created between the insert
		and the glass, which also helps Indow window inserts reduce outside noise.
	</p>
	<p>
		Old windows receiving storm windows might need some restoration before this
		type of performance can be expected. Meanwhile, replacement windows will
		bring the performance described as soon as they are installed. Storm windows
		can provide different needs—acoustic or UV protection—in just one room or
		window that fits that space’s needs.
	</p>
	<p>
		Analyze your space to determine your needs. Is noise an issue for your? Then
		storm windows are probably the way to go. You can read our full comparison
		of <a href="/resources/storm-windows-guide/replacement"
			>storm windows vs replacement windows</a
		> to get a better idea.
	</p>
	<Image
		alt="person walking by restored window"
		src={WomanWalkingByRestoredWindowImage}
	/>
</Section>

<Section>
	<h2>How to Get Storm Windows</h2>
	<p>
		All inserts are backed by a Snug Fit Guarantee because of our laser
		measurement system. Here’s how we do it:
	</p>
	<p>
		We send you a laser measure kit with a fun laser measure tool (pew pew). It
		works just like a flashlight—with the press of a button, you <a
			href="/about-indow/how-to-order"
		>
			get the dimensions
		</a>. Enter the measurements displayed on the tool into our online portal.
		It checks for accuracy.
	</p>
	<p>
		Our fit experts check the measurements for accuracy (again). If everything
		looks good, dimensions are on their way to becoming your custom insert. It’s
		that easy.
	</p>
	<p>
		We developed the tool and software (with error detecting algorithm) because
		it’s the best way we know how to seal windows for winter.
	</p>
</Section>

<Reviews />
